/**
 * The React side of joining a board screen to everyone else on it.
 *
 * All the wiring lives in shared-board.ts; this only hands it the browser's own
 * pieces and turns "who is connected" into something the screen can render.
 *
 * A board address that is missing — an old link, or a screen opened by hand — simply
 * draws locally rather than showing an error.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Point } from '@opal/shared';

import type { BoardHandle } from './BoardCanvas.js';
import type { ConnectionNotice } from './connection.js';
import type { BoardIdentity } from './presence.js';
import type { PageActions } from './board-pages.js';
import { createSharedBoard, type Frames, type SharedBoard } from './shared-board.js';
import { connectBoard, syncUrl, type BoardSync } from './sync.js';

export interface SharedBoardWiring {
  /** Handed to the canvas once its layers exist. */
  readonly onReady: (board: BoardHandle) => void;
  /** Where this person's hand is, so everyone else can see it. */
  readonly onPointerMove: (point: Point | null) => void;
  /**
   * Who else is connected right now, by id.
   *
   * A new array only when the people change, not when their cursors move — the
   * participants panel would otherwise re-render several times a second.
   */
  readonly online: readonly string[];
  /** What to say about the connection, if anything (SRS D5). */
  readonly notice: ConnectionNotice;
  /** Try the connection again now. */
  readonly onRetry: () => void;
  /** SRS C6 - take back the last thing this person did, and only theirs. */
  readonly undo: () => void;
  readonly redo: () => void;
  /** SRS D6 - whether this teacher is presenting, and the toggle for it. */
  readonly presenting: boolean;
  readonly setPresenting: (presenting: boolean) => void;
  /** SRS D6 - true for a student who has scrolled away from the teacher's view. */
  readonly canReturn: boolean;
  readonly returnToPresenter: () => void;
  /** SRS C1 - the pages of this board, for the tab strip along the bottom. */
  readonly pages: PageActions;
}

const frames: Frames = {
  request: (run) => window.requestAnimationFrame(run),
  cancel: (handle) => {
    window.cancelAnimationFrame(handle);
  },
};

/** Opens the real connection, carrying a student's place in the class (SRS D4). */
function connect(identity: BoardIdentity, boardId: string): BoardSync {
  return connectBoard(boardId, syncUrl(window.location), {
    participantId: identity.participantId,
  });
}

/** Whether the people on the board are the same ones, so the panel is left alone. */
function sameIds(current: readonly string[], next: readonly string[]): boolean {
  return current.length === next.length && current.every((id, index) => id === next[index]);
}

/** The plain callbacks the screen hands to its buttons, each stable across renders. */
function useBoardActions(shared: SharedBoard) {
  const onPointerMove = useCallback(
    (point: Point | null) => {
      shared.pointerMoved(point);
    },
    [shared],
  );
  const onRetry = useCallback(() => {
    shared.retry();
  }, [shared]);
  const undo = useCallback(() => {
    shared.undo();
  }, [shared]);
  const redo = useCallback(() => {
    shared.redo();
  }, [shared]);
  const returnToPresenter = useCallback(() => {
    shared.returnToPresenter();
  }, [shared]);

  return { onPointerMove, onRetry, undo, redo, returnToPresenter };
}

export function useSharedBoard(identity: BoardIdentity, boardId: string | null): SharedBoardWiring {
  const [online, setOnline] = useState<readonly string[]>([]);
  const [notice, setNotice] = useState<ConnectionNotice>('none');
  const [canReturn, setCanReturn] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const shared: SharedBoard = useMemo(() => createSharedBoard({ connect, frames }), []);
  // The canvas is built by a child of this screen, so it is ready before this screen's
  // own effect runs. It is kept here and handed over as soon as there is a connection
  // to hand it to.
  const board = useRef<BoardHandle | null>(null);
  const actions = useBoardActions(shared);

  useEffect(() => {
    shared.onOnline((userIds) => {
      setOnline((current) => (sameIds(current, userIds) ? current : userIds));
    });
    shared.onNotice(setNotice);
    shared.onCanReturn(setCanReturn);
    if (!boardId) return undefined;
    if (board.current) shared.attachBoard(board.current);
    shared.open(identity, boardId);
    return () => {
      shared.close();
      setOnline([]);
      setPresenting(false);
    };
  }, [shared, identity, boardId]);

  const onReady = useCallback(
    (ready: BoardHandle) => {
      board.current = ready;
      shared.attachBoard(ready);
    },
    [shared],
  );

  const present = useCallback(
    (on: boolean) => {
      setPresenting(on);
      shared.setPresenting(on);
    },
    [shared],
  );

  return {
    onReady,
    online,
    notice,
    presenting,
    setPresenting: present,
    canReturn,
    pages: shared.pages,
    ...actions,
  };
}
